import { getDeliveryByEventName, getProductImageURL } from '@noths/polaris-client-utils';
import type {
  ProductCardBadge,
  ProductCardProps,
} from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode, Price } from '@noths/polaris-dev-ts-types';
import { addDays } from 'date-fns';

import type { Product } from 'src/redux/products/types/Product';

/**
 * The card props that come from the product itself. The handlers, the favourite
 * state and `priority` are supplied by the call site, because those are about the
 * card's place on the page rather than about the product.
 */
export type ProductCardDataProps = Pick<
  ProductCardProps,
  'badge' | 'delivery' | 'isNew' | 'product' | 'promoteAsSale' | 'rating'
>;

interface AdaptProductToProductCardPropsOptions {
  currencyCode: CurrencyCode;
  /**
   * Whether every card in this listing comes from the same partner, as on a partner
   * page. Suppresses the badges that describe the partner rather than the product:
   * see `BADGE_RULES`.
   *
   * Required rather than defaulted for the same reason as `showMultipleImages`: a new
   * grid surface should have to state which it is, not inherit an arm by accident.
   */
  isSinglePartnerListing: boolean;
  /**
   * Passed in so that a list of products dates every delivery from the same instant,
   * and so that the date is deterministic in tests. Defaults for a single call.
   */
  now?: Date;
  /**
   * Whether the site is presenting discounts as a sale rather than as an offer. Set
   * site-wide in Contentstack, so it is read from `useDiscountPresentationMode` at
   * the call site rather than derived from the product: no discount in the catalogue
   * distinguishes the two.
   *
   * Required rather than defaulted for the same reason as `showMultipleImages`.
   */
  promoteAsSale: boolean;
  /**
   * Whether the card may show the product's other images, from the
   * `plp_product_card_image` experiment. Required rather than defaulted, so that
   * removing it once the experiment ends is a compile error at the call site rather
   * than a silent change of arm.
   */
  showMultipleImages: boolean;
}

/**
 * Ordered by priority: the card has a single badge slot, so the first rule that
 * applies takes it. Same shape as the PDP's `getProductBadge` and the recommendations
 * carousels, so every surface resolves its badge the same way.
 *
 * Most Loved comes first by product decision, and the data supports it. On 598 prod
 * products, Top Brand is on 16% against Most Loved's 2%, so Most Loved is much the
 * more telling signal, and 43% of Most Loved products are also Top Brand, meaning the
 * other order would suppress nearly half of the Most Loved badges.
 *
 * "Most Loved" and "Top Brand" are the customer-facing names for `highly_rated` and
 * `star_brand`.
 */
const BADGE_RULES: {
  applies: (product: Product) => boolean;
  badge: ProductCardBadge;
  /**
   * The badge describes the partner rather than the product, so it is skipped on a
   * listing scoped to one partner: there it is on every card or none, and carries no
   * information a customer can act on. The partner page's own recommendations rail
   * omits it for the same reason.
   */
  describesThePartner?: boolean;
}[] = [
  {
    applies: ({ highlyRated }) => Boolean(highlyRated),
    badge: { icon: 'mostLoved', label: 'Most Loved' },
  },
  {
    applies: ({ starBrand }) => Boolean(starBrand),
    badge: { icon: 'topBrand', label: 'Top Brand' },
    describesThePartner: true,
  },
];

const adaptBadge = (
  product: Product,
  isSinglePartnerListing: boolean,
): ProductCardBadge | undefined =>
  BADGE_RULES.find(
    ({ applies, describesThePartner }) =>
      !(describesThePartner && isSinglePartnerListing) && applies(product),
  )?.badge;

const priceInCurrency = (prices: Price[] | null, currencyCode: CurrencyCode) =>
  prices?.find(({ currency }) => currency === currencyCode)?.amount;

/**
 * The card takes a structured date and cost and words the delivery line itself,
 * so every surface reads identically.
 *
 * A product with no quoted option gets no delivery line. Nothing is lost by that:
 * the card drops a line it cannot date anyway. An unpurchasable product is
 * skipped too, since the card shows "Out of stock" in that slot and ignores
 * `delivery` entirely.
 */
const adaptDelivery = (
  { delivery, deliveryByEvent, purchasable }: Product,
  now: Date,
): ProductCardProps['delivery'] => {
  if (!purchasable || delivery === null) {
    return undefined;
  }

  /* The store holds the raw event key (`christmas`) and the card renders it as
     "Est to arrive by {eventName}", so it needs the display name. Not
     `getDeliveryByEventLabel`, which prefixes it ("Arrives by Christmas") and
     would read as "Est to arrive by Arrives by Christmas". The lookup is typed as
     always returning a value but is a plain object index, so an unrecognised key
     falls through to no event rather than to `undefined.name`. */
  const eventName = deliveryByEvent?.arriveByEvent
    ? getDeliveryByEventName(deliveryByEvent.eventName)?.name
    : undefined;

  return {
    /* The store holds the delivery cost in major units, as the listings API gives
       it (3.99 for £3.99), where `ProductCardDelivery.cost` is in minor units like
       every other price the card takes. The rounding is load-bearing, not
       cosmetic: 3.99 * 100 is 399.00000000000006. */
    cost: Math.round(delivery.cost * 100),
    date: addDays(now, delivery.days),
    ...(eventName ? { eventName } : {}),
  };
};

/**
 * Maps a store product onto the design system `ProductCard`.
 *
 * Deliberately thin: whether a product is discounted, the discount percentage and
 * the out-of-stock treatment are all derived by the card from `price`,
 * `originalPrice` and `availability`, so `onSale` and `salePercentage` are not
 * passed through. `promoteAsSale` is the exception, since how a discount is
 * presented is a site-wide decision rather than a property of the product.
 */
export const adaptProductToProductCardProps = (
  product: Product,
  {
    currencyCode,
    isSinglePartnerListing,
    now = new Date(),
    promoteAsSale,
    showMultipleImages,
  }: AdaptProductToProductCardPropsOptions,
): ProductCardDataProps => {
  const {
    averageRating,
    code,
    detailPageHref,
    images,
    isNew,
    partner,
    preSalePrices,
    price,
    purchasable,
    title,
  } = product;

  const delivery = adaptDelivery(product, now);
  const originalPrice = priceInCurrency(preSalePrices, currencyCode);
  /* The control arm of the `plp_product_card_image` experiment. The card has no
     gallery switch of its own: it draws a swipe carousel above one image and a plain
     frame at one, so handing it the hero alone is what turns the gallery off. */
  const cardImages = showMultipleImages ? images : images.slice(0, 1);
  const badge = adaptBadge(product, isSinglePartnerListing);

  return {
    ...(badge ? { badge } : {}),
    ...(delivery ? { delivery } : {}),
    isNew,
    product: {
      availability: purchasable ? 'in_stock' : 'out_of_stock',
      // The store keeps the numeric product code; the OOUX interface types it as a
      // string, since the basket API treats it as one.
      code: String(code),
      currencyCode,
      /* The array is handed over as it is. The card decides how many to show: it caps
         at five, renders a swipe carousel above one image and a plain frame at one,
         and shows a placeholder for none. So a product with a single image looks
         exactly as it did, whichever arm of the experiment it is in. */
      media: cardImages.map((image, index) => ({
        /* Only the primary image is described. The card labels each slide "N of M"
           and wraps every one in a link named by the product, so repeating the title
           on each would announce the product several times over. */
        alt: index === 0 ? title : '',
        type: 'image',
        /* `ProductMedia.url` must be fully resolved. The store holds the URL with
           its `{size}` token still in place, and the card does not resolve it. */
        url: getProductImageURL(image),
      })),
      ...(originalPrice !== undefined ? { originalPrice } : {}),
      partner: { name: partner.name, shortcode: partner.shortcode },
      price: price.amount,
      title,
      url: detailPageHref,
    },
    promoteAsSale,
    ...(averageRating !== undefined ? { rating: averageRating } : {}),
  };
};
